// app/frontend/components/inbox/MessageList.tsx

import { useRef, useEffect } from 'react'
import { Lock } from 'lucide-react'
import { router } from '@inertiajs/react'
import { MessageBubble } from './MessageBubble'
import { SystemMessage } from './SystemMessage'
import { BlurredMessage } from '@/components/conversation/BlurredMessage'
import { Button } from '@/components/ui/Button'
import { groupMessagesByDate } from '@/lib/dateUtils'
import routes from '@/routeHelpers'
import type { Message, TranslationState } from '@/types/conversation'

interface MessageListProps {
  messages: Message[]
  translations: Record<string, TranslationState>
  onTranslate: (messageId: string) => void
  accountWebsite?: string | null
}

function UpgradeBanner() {
  return (
    <div className="flex flex-col items-center gap-2 py-4">
      <div className="flex items-center gap-4 w-full">
        <div className="flex-1 border-t border-border" />
        <div className="flex items-center gap-2 text-muted-foreground">
          <Lock className="size-3.5" />
          <span className="text-xs font-medium">Message history limited on Free plan</span>
        </div>
        <div className="flex-1 border-t border-border" />
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={() => router.visit(routes.billing.plans.path())}
        className="cursor-pointer"
      >
        Upgrade to unlock
      </Button>
    </div>
  )
}

export function MessageList({
  messages,
  translations,
  onTranslate,
  accountWebsite
}: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesByDate = groupMessagesByDate(messages)
  const isInitialMount = useRef(true)
  const hasBlurredMessages = messages.some(m => m.blurred)

  // Find the index of the last blurred message to place the banner after it
  let lastBlurredIndex = -1
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].blurred) { lastBlurredIndex = i; break }
  }

  useEffect(() => {
    if (!messagesEndRef.current) return

    if (isInitialMount.current) {
      isInitialMount.current = false
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' })
      })
    } else {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-6 space-y-6">
      {messagesByDate.map(({ date, messages: dayMessages }) => (
        <div key={date}>
          <div className="flex items-center gap-4 my-4">
            <div className="flex-1 border-t border-border" />
            <span className="text-xs text-muted-foreground">{date}</span>
            <div className="flex-1 border-t border-border" />
          </div>
          <div className="space-y-4">
            {dayMessages.map((message) => {
              const isLastBlurred = hasBlurredMessages &&
                messages.indexOf(message) === lastBlurredIndex

              return (
                <div key={message.id}>
                  {message.blurred ? (
                    <BlurredMessage
                      isFromAgent={message.sender_type === 'User'}
                      content={message.content}
                    />
                  ) : message.message_type === 'system' ? (
                    <SystemMessage message={message} />
                  ) : (
                    <MessageBubble
                      message={message}
                      isFromAgent={message.sender_type === 'User'}
                      translation={translations[message.id]}
                      onTranslate={onTranslate}
                      accountWebsite={accountWebsite}
                    />
                  )}
                  {isLastBlurred && <UpgradeBanner />}
                </div>
              )
            })}
          </div>
        </div>
      ))}
      <div ref={messagesEndRef} />
    </div>
  )
}

export default MessageList
